import { useState, useEffect } from "react";
import { useTheme } from "@emotion/react";
import defaultTheme from "@indeed/ifl-themes";
import type { IndeedTheme } from "@indeed/ifl-themes";

export type DefaultOptions = {
  defaultIndex?: number;
};

const useBreakpointIndex = (options: DefaultOptions = {}): number => {
  const theme = useTheme() as IndeedTheme;
  const { defaultIndex = 0 } = options;
  const breakpoints = (theme && theme.breakpoints) || defaultTheme.breakpoints;
  if (typeof defaultIndex !== "number") {
    throw new TypeError(
      `Default breakpoint index should be a number. Got: ${defaultIndex}, ${typeof defaultIndex}`
    );
  } else if (defaultIndex < 0 || defaultIndex > breakpoints.length - 1) {
    throw new RangeError(
      `Default breakpoint index out of range. Theme has ${breakpoints.length} breakpoints, got index ${defaultIndex}`
    );
  }

  const [value, setValue] = useState(defaultIndex);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      (window && typeof window.matchMedia === "undefined")
    ) {
      return;
    }
    const getIndex = (): number =>
      (breakpoints as string[]).filter(
        (bp) => window.matchMedia(`screen and (min-width: ${bp})`).matches
      ).length;

    const onResize = (): void => {
      const newValue = getIndex();
      if (value !== newValue) {
        setValue(newValue);
      }
    };

    onResize();
    window.addEventListener("resize", onResize);

    return () => window.removeEventListener("resize", onResize);
  }, [breakpoints, value]);

  return value;
};

export default useBreakpointIndex;
